import { CompanyType, PathsV1VehicleSearchPostParametersQueryExtend } from "@/api-schema-enums";
import { useBranchSearchQuery } from "@/common/api/branch/use-branch-search.query";
import { useCompanySearchQuery } from "@/common/api/company/use-company-search.query";
import { useFitSettingsGetQuery } from "@/common/api/fit-settings/use-fit-settings-get.query";
import { useRelocationOrderBcaInitMutation } from "@/common/api/relocation-order-flow/use-relocation-order-bca-init.mutation";
import { useVehicleByVinQuery } from "@/common/api/vehicle/use-vehicle-by-vin.query";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { buttonVariants } from "@/components/ui/button";
import { Loader, LoaderSize } from "@/components/ui/loader";
import { isAxiosError } from "axios";
import { CircleAlertIcon } from "lucide-react";
import { type ReactElement, type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Link, useLocation, useSearch } from "wouter";
import { getAlreadyOpenRelocationOrder, normalizeVinParameter } from "./_helpers/bca-create.helpers";

const VEHICLE_EXTEND = [PathsV1VehicleSearchPostParametersQueryExtend.CurrentBranch];

export function BcaCreateView(): ReactElement {
  const { t } = useTranslation(["fit", "common"]);
  const [, navigate] = useLocation();
  const search = useSearch();
  const vin = normalizeVinParameter(new URLSearchParams(search).get("vin"));
  const autoCreateStartedRef = useRef(false);
  const [createError, setCreateError] = useState<{ detail?: string } | null>(null);
  const [otherOpenRelocationOrderUuid, setOtherOpenRelocationOrderUuid] = useState<string | null>(null);

  const {
    data: vehicle,
    isLoading: vehicleIsLoading,
    isError: vehicleIsError,
  } = useVehicleByVinQuery(vin ?? "", { parameters: { extend: VEHICLE_EXTEND } });
  const {
    data: fitSettings,
    isLoading: fitSettingsAreLoading,
    isError: fitSettingsLoadFailed,
  } = useFitSettingsGetQuery();
  const bcaCompanyUuid = fitSettings?.bcaCompany?.uuid;

  const {
    data: bcaBranchesResponse,
    isLoading: bcaBranchesAreLoading,
    isError: bcaBranchesLoadFailed,
  } = useBranchSearchQuery({
    options: {
      parameters: { pageSize: -1 },
      body: { company: bcaCompanyUuid ? [bcaCompanyUuid] : [] },
    },
    enabled: !!bcaCompanyUuid,
  });

  const {
    data: transportCompaniesResponse,
    isLoading: transportCompaniesAreLoading,
    isError: transportCompaniesLoadFailed,
  } = useCompanySearchQuery({
    options: {
      parameters: { pageSize: -1 },
      body: { type: [CompanyType.Transport] },
    },
  });

  const { mutateAsync, isPending } = useRelocationOrderBcaInitMutation();

  const bcaBranches = bcaBranchesResponse?.data ?? [];
  const transportCompanies = transportCompaniesResponse?.data ?? [];
  const destinationBranchUuid = bcaBranches.length === 1 ? bcaBranches[0].uuid : null;
  const transportCompanyUuid = transportCompanies.length === 1 ? transportCompanies[0].uuid : null;

  const isLoading =
    !!vin &&
    (vehicleIsLoading ||
      fitSettingsAreLoading ||
      transportCompaniesAreLoading ||
      (!!bcaCompanyUuid && bcaBranchesAreLoading));
  const hasLoadError = fitSettingsLoadFailed || bcaBranchesLoadFailed || transportCompaniesLoadFailed;
  const canAutoCreate =
    !!vin &&
    !isLoading &&
    !hasLoadError &&
    !vehicleIsError &&
    !!vehicle?.currentBranch &&
    !!destinationBranchUuid &&
    !!transportCompanyUuid;

  useEffect(() => {
    if (!canAutoCreate || !vin || !destinationBranchUuid || !transportCompanyUuid || autoCreateStartedRef.current) {
      return;
    }
    autoCreateStartedRef.current = true;

    const createRelocationOrder = async (): Promise<void> => {
      try {
        const relocationOrder = await mutateAsync({ vin, destinationBranchUuid, transportCompanyUuid });
        toast.success(t("fit:BcaRelocationOrderCreated"));
        navigate(`~/fit/relocation-order/${relocationOrder.uuid}`, { replace: true });
      } catch (error) {
        const existingRelocationOrder = getAlreadyOpenRelocationOrder(error);
        if (existingRelocationOrder?.isBcaOrder) {
          toast.info(t("fit:BcaRelocationOrderAlreadyOpen"));
          navigate(`~/fit/relocation-order/${existingRelocationOrder.relocationOrderUuid}`, { replace: true });
          return;
        }
        if (existingRelocationOrder) {
          setOtherOpenRelocationOrderUuid(existingRelocationOrder.relocationOrderUuid);
          return;
        }

        const backendMessage = isAxiosError(error) ? error.response?.data?.message : undefined;
        setCreateError({ detail: typeof backendMessage === "string" ? backendMessage : undefined });
      }
    };

    void createRelocationOrder();
  }, [canAutoCreate, destinationBranchUuid, mutateAsync, navigate, t, transportCompanyUuid, vin]);

  const renderError = (title: string, description?: ReactNode): ReactElement => (
    <Alert variant="error">
      <CircleAlertIcon />
      <AlertTitle>{title}</AlertTitle>
      {description ? <AlertDescription>{description}</AlertDescription> : null}
    </Alert>
  );

  const renderContactAdminsError = (title: string, reasons: string[]): ReactElement =>
    renderError(
      title,
      <>
        {reasons.map((reason) => (
          <p key={reason}>{reason}</p>
        ))}
        <p className="font-medium">{t("fit:BcaContactAlphanetAdmins")}</p>
      </>,
    );

  const renderContent = (): ReactElement => {
    if (!vin) {
      return renderError(t("fit:BcaMissingVinTitle"), t("fit:BcaMissingVinDescription"));
    }

    if (otherOpenRelocationOrderUuid) {
      return (
        <div className="flex flex-col gap-4">
          {renderError(t("fit:BcaOtherOpenOrderTitle"), t("fit:BcaOtherOpenOrderDescription"))}
          <div className="flex flex-row justify-end">
            <Link href={`~/fit/relocation-order/${otherOpenRelocationOrderUuid}`} className={buttonVariants()}>
              {t("fit:BcaOpenOtherOrder")}
            </Link>
          </div>
        </div>
      );
    }

    if (createError) {
      return renderError(t("fit:BcaRelocationOrderCreateError"), createError.detail);
    }

    if (isLoading || canAutoCreate || isPending) {
      return (
        <div className="flex min-h-[40vh] items-center justify-center">
          <Loader size={LoaderSize.Large} />
        </div>
      );
    }

    if (hasLoadError) {
      return renderError(t("fit:BcaLoadErrorTitle"), t("fit:BcaLoadErrorDescription"));
    }

    if (vehicleIsError || !vehicle) {
      return renderError(t("fit:BcaVehicleNotFoundTitle"), t("fit:BcaVehicleNotFoundDescription", { vin }));
    }

    if (!bcaCompanyUuid) {
      return renderError(t("fit:BcaCompanyNotConfiguredTitle"), t("fit:BcaCompanyNotConfiguredDescription"));
    }

    if (bcaBranches.length === 0) {
      return renderContactAdminsError(t("fit:BcaNoBranchesTitle"), [t("fit:BcaNoBranchesDescription")]);
    }

    if (transportCompanies.length === 0) {
      return renderContactAdminsError(t("fit:BcaNoTransportCompaniesTitle"), [
        t("fit:BcaNoTransportCompaniesDescription"),
      ]);
    }

    if (bcaBranches.length > 1 || transportCompanies.length > 1) {
      return renderContactAdminsError(t("fit:BcaAutoCreateNotPossibleTitle"), [
        ...(bcaBranches.length > 1 ? [t("fit:BcaMultipleBranches")] : []),
        ...(transportCompanies.length > 1 ? [t("fit:BcaMultipleTransportCompanies")] : []),
      ]);
    }

    return renderError(t("fit:BcaVehicleHasNoLocationTitle"), t("fit:BcaVehicleHasNoLocationDescription"));
  };

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Default}
      title={t("fit:BcaRelocationOrderTitle")}
      subTitle={t("fit:BcaRelocationOrderDescription")}
    >
      {renderContent()}
    </CenteredLayout>
  );
}
